import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";

import styles from "./header.module.css";
import OutsideAlerter from "../../utils/OutsideAlerter";
import { UserContext } from "../../context/UserContext";
import logoName from "../../assets/logoName.png";

export const Header = () => {
  const { t, i18n } = useTranslation("navbar");
  const { user, userLogout, userData } = React.useContext(UserContext);
  const [menuOpen, setMenuOpen] = useState(false); // user menu
  const [navOpen, setNavOpen] = useState(false); // mobile nav
  const [langModalOpen, setLangModalOpen] = useState(false);
  const langFirstRef = useRef(null);
  const langTriggerRef = useRef(null);
  const menuRef = useRef(null);
  const navRef = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const onChangeLang = (lng) => {
    i18n.changeLanguage(lng);
    try {
      localStorage.setItem("i18nextLng", lng);
    } catch (e) {}
    setLangModalOpen(false);
    // return focus to trigger
    setTimeout(() => langTriggerRef.current?.focus(), 40);
  };

  // close menus on Esc
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setNavOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const role = userData?.userType?.category || "user";

  const navItems = [
    { to: "/", label: t("home"), show: true },
    { to: "/profile", label: t("profile"), show: true },
    // { to: "/planner", label: "Planner", show: true },
    // { to: "/library", label: "Biblioteca", show: true },
    // { to: "/meeting", label: "Encontros", show: true },
  ];

  // role-specific entries
  if (role === "professional") {
    navItems.push({
      to: "/prouser",
      label: "Agenda (Pro)",
      show: true,
    });
  }
  if (role === "admin") {
    navItems.push({
      to: "/prouser",
      label: "Agenda (Pro)",
      show: true,
    });
    navItems.push({ to: "/admin", label: "Admin", show: true });
  }

  const displayName = userData?.displayName || user?.displayName || "Usuário";
  const initials = displayName
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header className={styles.header}>
      <div className={styles.inner}>
        <div className={styles.left}>
          <Link to="/" className={styles.brand} aria-label="Ibhava — Home">
            <img src={logoName} alt="" />
          </Link>
        </div>

        <nav className={styles.navDesktop} aria-label="Navegação principal">
          {navItems.map((item) =>
            item.show ? (
              <Link
                key={item.to}
                to={item.to}
                className={
                  pathname === item.to ? styles.activeLink : styles.navLink
                }
              >
                {item.label}
              </Link>
            ) : null
          )}
        </nav>

        <div className={styles.right}>
          {/* mobile hamburger */}
          <button
            className={styles.hamburger}
            aria-label={navOpen ? "Fechar menu" : "Abrir menu"}
            onClick={() => setNavOpen((v) => !v)}
          >
            <span className="material-icons" aria-hidden>
              menu
            </span>
          </button>

          {/* mobile nav overlay */}
          {navOpen && (
            <div
              className={styles.mobileNav}
              role="dialog"
              aria-modal="true"
              ref={navRef}
            >
              <div className={styles.mobileNavInner}>
                <button
                  className={styles.closeMobile}
                  onClick={() => setNavOpen(false)}
                  aria-label="Fechar"
                >
                  <span className="material-icons">close</span>
                </button>
                <div className={styles.mobileLinks}>
                  {navItems.map((n) => (
                    <Link
                      key={n.to}
                      to={n.to}
                      onClick={() => setNavOpen(false)}
                      className={styles.mobileLink}
                    >
                      {n.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* quick icons */}
          {/* LANGUAGE TRIGGER ICON */}
          <div className={styles.lang}>
            <button
              ref={langTriggerRef}
              className={styles.langIcon}
              aria-haspopup="dialog"
              aria-expanded={langModalOpen}
              aria-controls="lang-modal"
              onClick={() => setLangModalOpen((s) => !s)}
              title={i18n.language === "pt" ? "Português" : "English"}
            >
              {/* globe icon */}
              <i className="material-symbols-outlined">language</i>
              <span className={styles.langAbbr}>
                {i18n.language?.toUpperCase?.() || "PT"}
              </span>
            </button>
          </div>
          <OutsideAlerter setMenuButton={setMenuOpen}>
            <div className={styles.userWrap}>
              <button
                aria-haspopup="true"
                aria-expanded={menuOpen}
                className={styles.userBtn}
                onClick={() => setMenuOpen((s) => !s)}
              >
                {userData?.photoURL ? (
                  <img
                    src={userData.photoURL}
                    alt={displayName}
                    className={styles.avatar}
                  />
                ) : (
                  <div className={styles.avatarPlaceholder}>{initials}</div>
                )}
                <span className={styles.userShort}>
                  {displayName.split(" ")[0]}
                </span>
                <span className="material-icons" aria-hidden>
                  expand_more
                </span>
              </button>

              {menuOpen && (
                <div className={styles.userMenu} role="menu" ref={menuRef}>
                  <Link
                    to="/account"
                    onClick={() => setMenuOpen(false)}
                    className={styles.userMenuItem}
                  >
                    {t("account")}
                  </Link>
                  <button onClick={userLogout} className={styles.userMenuItem}>
                    {t("logout")}
                  </button>
                </div>
              )}
            </div>
          </OutsideAlerter>
        </div>
      </div>
      {/* LANGUAGE MODAL */}
      {langModalOpen && (
        <div
          id="lang-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lang-title"
          className={styles.langModalOverlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) setLangModalOpen(false);
          }}
        >
          <div className={styles.langModal} role="document">
            <h3 id="lang-title" className={styles.langModalTitle}>
              Idioma / Language
            </h3>

            <div className={styles.langOptions}>
              <button
                ref={langFirstRef}
                className={`${styles.langOption} ${
                  i18n.language === "pt" ? styles.langActive : ""
                }`}
                onClick={() => onChangeLang("pt")}
                aria-pressed={i18n.language === "pt"}
              >
                Português (PT)
              </button>

              <button
                className={`${styles.langOption} ${
                  i18n.language === "en" ? styles.langActive : ""
                }`}
                onClick={() => onChangeLang("en")}
                aria-pressed={i18n.language === "en"}
              >
                English (EN)
              </button>

              {/* <button
                      className={`${styles.langOption} ${
                        i18n.language === "it" ? styles.langActive : ""
                      }`}
                      onClick={() => onChangeLang("it")}
                      aria-pressed={i18n.language === "it"}
                    >
                      Italian (IT)
                    </button> */}
            </div>

            <button
              className={styles.langClose}
              onClick={() => setLangModalOpen(false)}
              aria-label="Fechar seleção de idioma"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
